// Build-picture helpers: frame the mic and render a square thumbnail off screen. Moved out of App.js unchanged.
import * as THREE from 'three'

// Robust version that keeps your old named-box behavior but falls back to picking the largest renderable mesh
// replace your computeBoxFromNames with this robust version
export function computeBoxFromNames(root, names = [], { requireVisible = true } = {}) {
  if (!root) return new THREE.Box3().makeEmpty()

  // try exact-name aggregation first (your old behaviour)
  try {
    const box = new THREE.Box3()
    let any = false
    for (const n of names) {
      const obj = root.getObjectByName(n)
      if (!obj) continue
      if (requireVisible && obj.visible === false) continue
      const b = new THREE.Box3().setFromObject(obj)
      if (!b.isEmpty()) {
        any ? box.union(b) : (box.copy(b), (any = true))
      }
    }
    if (any && !box.isEmpty()) return box
  } catch (e) {
    // fall through to fallback
  }

  // fallback: pick the largest visible mesh (robust for different gltf layout)
  try {
    let best = null
    let bestR = 0
    root.traverse((obj) => {
      if (!obj) return
      if (requireVisible && obj.visible === false) return
      if (!obj.isMesh && !obj.isSkinnedMesh) return
      const b = new THREE.Box3().setFromObject(obj)
      if (!b.isEmpty()) {
        const s = new THREE.Sphere()
        b.getBoundingSphere(s)
        if (isFinite(s.radius) && s.radius > bestR) {
          bestR = s.radius
          best = obj
        }
      }
    })
    if (best) return new THREE.Box3().setFromObject(best)
  } catch (e) {
    // fall through
  }

  // final fallback: whole-root bounds
  try {
    return new THREE.Box3().setFromObject(root)
  } catch (e) {
    return new THREE.Box3().makeEmpty()
  }
}

export function recenterControlsToBox(box, controls, camera, keepDistance = true) {
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const radius = Math.max(size.x, size.y, size.z) * 0.5 || 0.5

  controls.target.copy(center)

  if (!keepDistance) {
    const dir = new THREE.Vector3().subVectors(camera.position, center).normalize()
    const dist = Math.max(0.001, radius * 2.2)
    camera.position.copy(center).addScaledVector(dir, dist)
  }
  camera.updateProjectionMatrix()
  controls.update()
}

// === Helper: frame the mic and render a square PNG Blob (does NOT touch your on-screen camera) ===
export function frameAndRenderThumbnail({
  target, // THREE.Object3D: your mic root (use modelRef.current)
  scene, // THREE.Scene
  bgColor = '#ffffff',
  size = 180,
  fov = 35,
  pad = 1.2
}) {
  // Ensure transforms are current
  target.updateWorldMatrix(true, true)

  // Bounds of target only (not whole scene)
  const box = new THREE.Box3().setFromObject(target)
  const center = box.getCenter(new THREE.Vector3())
  const sizeVec = box.getSize(new THREE.Vector3())
  const radius = sizeVec.length() * 0.5 || 1

  // Dedicated square camera
  const camera = new THREE.PerspectiveCamera(fov, 1, 0.001, radius * 10)
  const halfFovRad = THREE.MathUtils.degToRad(fov / 2)
  const dist = (radius * pad) / Math.sin(halfFovRad)

  // A neutral, slightly-elevated, centered angle
  camera.position.set(center.x, center.y + radius * 0.15, center.z + dist)
  camera.lookAt(center)
  camera.updateProjectionMatrix()

  // Offscreen square canvas/renderer
  const offscreen = document.createElement('canvas')
  offscreen.width = size
  offscreen.height = size

  const renderer = new THREE.WebGLRenderer({ canvas: offscreen, antialias: true, alpha: false })
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  renderer.setPixelRatio(dpr)
  renderer.setSize(size, size, false)
  renderer.setClearColor(bgColor, 1)

  // Simple white backdrop behind the mic (prevents see-through)
  const backdrop = new THREE.Mesh(new THREE.PlaneGeometry(radius * 10, radius * 10), new THREE.MeshBasicMaterial({ color: bgColor }))
  backdrop.position.set(center.x, center.y, center.z - radius * 2)
  scene.add(backdrop)

  renderer.render(scene, camera)

  // Cleanup temp objects
  scene.remove(backdrop)
  backdrop.geometry.dispose()
  backdrop.material.dispose()
  renderer.dispose()

  // Return a PNG Blob of exactly size×size px
  return new Promise((resolve) => offscreen.toBlob((b) => resolve(b), 'image/png'))
}

// ======= START tuneAndCaptureThumbnail (auto-confirm if preview is hidden) =======
// ======= tuneAndCaptureThumbnail (hidden preview, auto confirm, yaw 7/8) =======
export async function tuneAndCaptureThumbnail(scene, opts) {
  opts = opts || {}
  const initialFrameFill = typeof opts.initialFrameFill === 'number' ? opts.initialFrameFill : 95
  const initialVBias = typeof opts.initialVBias === 'number' ? opts.initialVBias : 0.18
  const initialAnchorK = typeof opts.initialAnchorK === 'number' ? opts.initialAnchorK : 0.52
  const initialAzimuthDeg = typeof opts.initialAzimuthDeg === 'number' ? opts.initialAzimuthDeg : 0 // 0 = front, 45 = nice 3/4
  const thumbSize = typeof opts.thumbSize === 'number' ? opts.thumbSize : 180
  const rootName = typeof opts.rootName === 'string' ? opts.rootName : null
  const bgColor = typeof opts.bgColor !== 'undefined' ? opts.bgColor : 0xffffff
  // file type of the picture: PNG by default; the cart picture asks for JPEG (same look on the solid blue, a third of the size)
  const mimeType = opts.mimeType === 'image/jpeg' ? 'image/jpeg' : 'image/png'
  const quality = typeof opts.quality === 'number' ? opts.quality : 0.92

  const prev = document.getElementById('ohma-thumb-tuner')
  if (prev) prev.remove()

  // Overlay shell (not mounted by default)
  const wrap = document.createElement('div')
  wrap.id = 'ohma-thumb-tuner'
  const holder = document.createElement('div')
  const label = document.createElement('div')
  const hint = document.createElement('div')
  hint.textContent = '1/2 zoom, 3/4 vertical, 5/6 anchor, 7/8 yaw, 0 reset vertical, Enter confirm, Esc cancel'
  const btnRow = document.createElement('div')
  const btnClose = document.createElement('button')
  btnClose.textContent = 'Close'

  // Reuse existing renderer if provided — avoids creating a second WebGL context on iOS
  const existingRenderer = opts.renderer || null
  const canvas = existingRenderer ? existingRenderer.domElement : document.createElement('canvas')
  if (!existingRenderer) {
    canvas.width = thumbSize
    canvas.height = thumbSize
  }

  const renderer =
    existingRenderer ||
    new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance'
    })

  // Save original state so we can restore after capture
  const origPixelRatio = renderer.getPixelRatio()
  const origSize = renderer.getSize(new THREE.Vector2())
  const origClearColor = new THREE.Color()
  const origClearAlpha = renderer.getClearAlpha()
  renderer.getClearColor(origClearColor)

  renderer.toneMapping = THREE.NoToneMapping
  renderer.setPixelRatio(1)
  renderer.setSize(thumbSize, thumbSize, false)
  renderer.setClearColor(bgColor, 1)

  // Target root
  scene.updateMatrixWorld(true)
  let root = rootName ? scene.getObjectByName(rootName) : scene
  if (!root) root = scene

  function pickRenderableRoot(node) {
    let best = null,
      bestR = 0
    node.traverse((obj) => {
      if (!obj.visible) return
      if (!obj.isMesh && !obj.isSkinnedMesh) return
      const s = new THREE.Sphere()
      new THREE.Box3().setFromObject(obj).getBoundingSphere(s)
      if (isFinite(s.radius) && s.radius > bestR) {
        bestR = s.radius
        best = obj
      }
    })
    return best || node
  }
  root = pickRenderableRoot(root)

  // State
  let frameFill = initialFrameFill
  let vBias = initialVBias
  let anchorK = initialAnchorK
  let azimuthDeg = initialAzimuthDeg // new: yaw around Y in degrees

  // Promise plumbing
  let resolveFn
  const p = new Promise((res) => {
    resolveFn = res
  })

  function finish(cancelled) {
    window.removeEventListener('keydown', onKey)
    try {
      wrap.remove()
    } catch {}

    // Capture blob BEFORE restoring renderer size (resizing wipes pixel buffer)
    let blobDone = false
    const blobTimeout = setTimeout(() => {
      if (!blobDone) {
        blobDone = true
        restoreRenderer()
        resolveFn({ blob: null, frameFill, vBias, anchorK, azimuthDeg, cancelled: false })
      }
    }, 5000)

    canvas.toBlob((blob) => {
      if (!blobDone) {
        blobDone = true
        clearTimeout(blobTimeout)
        restoreRenderer()
        resolveFn({ blob: cancelled ? null : blob, frameFill, vBias, anchorK, azimuthDeg, cancelled })
      }
    }, mimeType, quality)
  }

  function restoreRenderer() {
    try {
      if (!existingRenderer) {
        renderer.dispose()
      } else {
        renderer.setPixelRatio(origPixelRatio)
        renderer.setSize(origSize.x, origSize.y, false)
        renderer.setClearColor(origClearColor, origClearAlpha)
        // The capture renders through its own camera, so the on-screen camera never needs restoring.
      }
    } catch {}
  }

  function renderOnce() {
    // Bounds
    const sphere = new THREE.Sphere()
    new THREE.Box3().setFromObject(root).getBoundingSphere(sphere)
    const R = Math.max(1e-6, sphere.radius)
    const C = sphere.center.clone()

    // Camera
    const cam = new THREE.PerspectiveCamera(35, 1, 0.01, 1000)
    cam.layers.mask = root.layers.mask

    const fovRad = THREE.MathUtils.degToRad(cam.fov)
    const baseDist = R / Math.tan(fovRad * 0.5)
    let dist = baseDist / (Math.max(1e-6, frameFill) / 100)
    const minBase = Math.max(0.02, 0.08 * R)
    if (dist < minBase) dist = minBase

    const viewH = 2 * dist * Math.tan(fovRad * 0.5)
    const yLift = vBias * viewH
    const targetY = C.y - R + anchorK * (2 * R)

    // Yaw around Y axis by azimuthDeg
    const az = THREE.MathUtils.degToRad(azimuthDeg)
    const camX = C.x + dist * Math.sin(az)
    const camZ = C.z + dist * Math.cos(az)

    cam.position.set(camX, targetY + yLift, camZ)
    cam.up.set(0, 1, 0)
    cam.lookAt(new THREE.Vector3(C.x, targetY, C.z))
    cam.aspect = 1
    cam.near = Math.min(0.01, dist * 0.1)
    cam.far = Math.abs(dist) + 10 * R
    cam.updateProjectionMatrix()

    // Minimal helpers
    const tempAmbient = new THREE.AmbientLight(0xffffff, 0.2)
    const backdrop = new THREE.Mesh(new THREE.PlaneGeometry(100 * R, 100 * R), new THREE.MeshBasicMaterial({ color: bgColor }))
    backdrop.position.set(C.x, C.y, C.z - 2 * R)
    scene.add(backdrop)

    renderer.render(scene, cam)

    scene.remove(backdrop)
    backdrop.geometry.dispose()
    backdrop.material.dispose()
    scene.remove(tempAmbient)

    label.textContent =
      `frameFill: ${frameFill.toFixed(1)}\n` + `vBias: ${vBias.toFixed(2)}\n` + `anchorK: ${anchorK.toFixed(2)}\n` + `azimuthDeg: ${azimuthDeg.toFixed(1)}`
  }

  // First paint
  renderOnce()

  // Auto confirm when preview is hidden
  if (!document.body.contains(wrap)) {
    finish(false)
    return p
  }

  // If you later want to show the preview, append these:
  //document.body.appendChild(wrap)
  //holder.appendChild(canvas)

  // Hotkeys
  function onKey(e) {
    let changed = false
    if (e.key === '1') {
      frameFill += 5
      changed = true
    }
    if (e.key === '2') {
      frameFill -= 5
      changed = true
    }
    if (e.key === '3') {
      vBias += 0.1
      changed = true
    }
    if (e.key === '4') {
      vBias -= 0.1
      changed = true
    }
    if (e.key === '5') {
      anchorK += 0.02
      changed = true
    }
    if (e.key === '6') {
      anchorK -= 0.02
      changed = true
    }
    if (e.key === '7') {
      azimuthDeg += 5
      changed = true
    } // rotate right
    if (e.key === '8') {
      azimuthDeg -= 5
      changed = true
    } // rotate left
    if (e.key === '0') {
      vBias = 0.0
      changed = true
    }

    if (changed) {
      e.preventDefault()
      renderOnce()
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      finish(false)
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      finish(true)
    }
  }

  btnClose.onclick = () => finish(true)
  window.addEventListener('keydown', onKey)

  return p
}

